import { PublicClientApplication } from "@azure/msal-browser";
import { AuthHelper } from "./AuthHelper";

export abstract class APIHelper {
  public static async CallAPI(
    msalInstance: PublicClientApplication,
    apiPayload: any,
    apiMethod: any
  ) {
    const accessToken = await AuthHelper.getAccessToken(msalInstance);
    if (accessToken !== "") {
      return apiMethod(accessToken, apiPayload);
    }
    return "Access token cannot be empty!";
  }
}

/**
 * Fetches a resource from the network with a specified timeout.
 *
 * This function wraps the native `fetch` API and adds a timeout mechanism.
 * If the fetch operation does not complete within the specified timeout,
 * the promise will be rejected with a timeout error.
 *
 * @throws {Error} Throws an error with the message "timeout" if the fetch operation exceeds the specified timeout.
 */
export const fetchWithTimeout = (
  url: string,
  options: any,
  timeout = 5000
): Promise<Response> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("timeout"));
    }, timeout);

    fetch(url, options)
      .then((resp) => {
        clearTimeout(timer);
        resolve(resp);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

/**
 * Makes a fetch request with retry logic and exponential backoff.
 *
 * @throws An error if all retry attempts fail.
 */
export const fetchWithRetry = async (
  src: string,
  options: any,
  retries = 3,
  backoff = 300
): Promise<Response> => {
  let lastStatus: number | undefined;
  let lastError: any;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const resp = await fetch(src, options);

      if (resp.ok) return resp;

      // Record the non-OK status and continue retrying
      lastStatus = resp.status;
      lastError = new Error(String(resp.status));
    } catch (err: any) {
      lastError = err;

      if (typeof err?.status === "number") {
        lastStatus = err.status;
      } else if (typeof err?.code === "number") {
        lastStatus = err.code;
      }
    }

    // Wait before retrying if attempts remain
    if (attempt < retries) {
      await new Promise((res) => setTimeout(res, backoff * (attempt + 1)));
    }
  }

  // Build a consistent error for the caller
  const message = lastStatus
    ? String(lastStatus)
    : lastError?.message || "UNKNOWN";
  const error: any = new Error(message);
  if (lastStatus) error.status = lastStatus;

  throw error;
};
